import { getTheme, themed, Button, Box, VBox, Text as Tx, } from "@bracket/ui";
import { Icon, type IconName } from "@components";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useMutation } from "@apollo/client";
import { UPDATE_NOTIFICATIONS_STATUS } from "@gql/mutations";
import type { Notifications_StatusMutation } from "@gql/types/graphql";
import { useAccount } from "wagmi";



export declare type NotificationProps = {
  id: string;
  title: string;
  body: string;
  created: string;
  iconName?: IconName;
  actionUrl?: string;
  actionLabel?: string;
  onRead?: () => Promise<void>;
  displayRead?: boolean;
};

export const Notification: React.FC<NotificationProps> = ({
  id,
  title,
  created,
  body,
  iconName,
  actionLabel,
  actionUrl,
  onRead,
  displayRead = true,
}) => {
	themed();

	const { address } = useAccount();
  const navigate = useNavigate();
  const [updateNotificationStatus, { loading: isUpdating }] =
    useMutation<Notifications_StatusMutation>(UPDATE_NOTIFICATIONS_STATUS, {
      context: {
        headers: {
          wallet: address,
        },
      },
    });

  const handleActionClick = (actionUrl: NotificationProps["actionUrl"]) => {
    if (actionUrl) {
      const isOuterRedirect = actionUrl.startsWith("http");

      if (isOuterRedirect) {
        window.location.href = actionUrl;
        return;
      }

      navigate(actionUrl);
    }
  };

  const handleReadClick = async () => {
    await updateNotificationStatus({
      variables: {
        status: JSON.stringify({
          [id]: "READ",
        }),
      },
    });

    if (onRead) {
      await onRead();
    }
  };

  return (
    <NotificationContainer>
      <TitleContainer>
        {iconName && <IconContainer><Icon name={iconName} color="paragraph" /></IconContainer>}
        <Tx color="heading">{title}</Tx>
      </TitleContainer>

      <Tx sm color="paragraph">{body}</Tx>
      <Tx sm color="subheading">{DateTime.fromISO(created).toFormat("DD")}</Tx>

      {displayRead && (
        <ReadButton
          variant="outline"
          size="md"
          label={ <Tx xsm color="surface_on">Read</Tx> }
          onClick={handleReadClick}
          disabled={isUpdating}
        />
      )}

      {actionUrl && (
        <NotificationActionButton
          label={<Tx xsm color="surface_on">{actionLabel ?? actionUrl}</Tx>}
          variant="text"
          size="sm"
          onClick={() => handleActionClick(actionUrl)}
        />
      )}
    </NotificationContainer>
  );
};



const NotificationContainer = styled(VBox)`
  gap: 12px;
  padding-top: 16px;
  padding-bottom: 16px;
  position: relative;
  &:not(:last-child) {
    border-bottom: 1px solid ${() => getTheme().surface_inverse_on};
  }
`;

const IconContainer = styled(Box)`
  background-color: ${() => getTheme().surface_container_highest};
  border-radius: 8px;
  width: 32px;
  height: 32px;
  justify-content: center;
  align-items: center;
`;

const TitleContainer = styled(Box)`
  align-items: center;
  gap: 12px;
  margin-right: auto;
`;

const NotificationActionButton = styled(Button)`
  display: flex;
  padding: 8px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  background-color: ${() => getTheme().surface_container_highest};
  width: fit-content;
`;

const ReadButton = styled(Button)`
	position: absolute;
  padding: 8px 16px;
	@media(max-width: 1023px) {
		right: 0;
		bottom: -15px;
		transform: translateY(-50%);
	}
	@media(min-width: 1024px) {
	  right: 0;
	  top: 50%;
		transform: translateY(-50%);
	}
`;
